import { useEffect, useState } from "react";
import { istFreigabeKandidat } from "./pluginGate";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useMarketplacePlugins } from "@/api/queries/plugins";
import { CatalogSourceAlert } from "@/components/plugin-catalog/CatalogStatus";
import { useBulkApprovePlugins } from "@/api/mutations/plugins";

// #288 (UC-6, Verkettung): Direkt nach der Freigabe eines Katalog-Entwurfs fragen, ob die
// enthaltenen Plugins auch freigeschaltet werden sollen — vorausgewählt mit Ja.
//
// ⚠ Das ist AUSDRÜCKLICH nur ein Bedien-Ablauf. Der Server koppelt die beiden Vorgänge
// nicht: „Eintrag in den Katalog aufgenommen" und „zur Nutzung freigegeben" bleiben zwei
// Entscheidungen mit zwei Audit-Datensätzen. Würde der Merge automatisch freischalten, könnte
// später niemand mehr unterscheiden, wer was entschieden hat — und ein zusammengeführter
// Entwurf wäre eine stille Berechtigung.
export function FreischaltenNachFreigabeDialog({
  marketplaceId,
  open,
  onOpenChange,
}: {
  marketplaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const katalog = useMarketplacePlugins(open ? marketplaceId : undefined);
  const bulk = useBulkApprovePlugins();
  const [abgewaehlt, setAbgewaehlt] = useState<Set<string>>(new Set());

  // Der Katalog wurde soeben in Git geändert — der zwischengespeicherte Stand ist von VOR
  // dem Merge und würde neue Einträge schlicht nicht enthalten.
  useEffect(() => {
    if (open) void katalog.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const offen = (katalog.data?.plugins ?? []).filter(istFreigabeKandidat);
  const gewaehlt = offen.filter((p) => !abgewaehlt.has(p.name));

  // ⚠ Review-Fund: Ein unlesbares Manifest kommt hier NICHT als `isError` an. Der Endpunkt
  // antwortet 200 mit `sources[].ok === false` und einer leeren Liste (seit #287 fängt der
  // Katalog-Service jeden Lesefehler ab, statt zu werfen). Ohne diese Prüfung wäre der
  // häufigste Störfall genau der leere Zustand, den #287 verboten hat: der Dialog schlösse
  // sich wortlos, der Admin hielte alles für erledigt — und die frisch gemergten Plugins
  // blieben unfreigeschaltet und unausgeliefert, ohne dass irgendwo etwas stünde.
  const quelleKaputt = (katalog.data?.sources ?? []).some((s) => !s.ok);
  const lesefehler = katalog.isError || quelleKaputt;

  // Nichts freizuschalten → gar nicht stören. Der Normalfall „ich habe nur die Beschreibung
  // korrigiert" soll keinen Dialog erzeugen.
  //
  // ⚠ Bedingung ist das VORHANDENSEIN gelesener Daten, nicht ein Lade-Flag: `isLoading` ist im
  // ersten Render-Durchlauf bereits false, obwohl noch nichts geholt wurde — der Dialog schloss
  // sich damit, bevor die erste Antwort da war. Und bei einem Lesefehler wird nie geschlossen:
  // dann wissen wir gerade nicht, ob es etwas zu tun gibt.
  useEffect(() => {
    if (open && katalog.data && !lesefehler && offen.length === 0) onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, katalog.data, lesefehler, offen.length]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="freischalten-dialog">
        <DialogHeader>
          <DialogTitle>Enthaltene Plugins freischalten?</DialogTitle>
          <DialogDescription>
            Der Katalog ist jetzt live. Freigeschaltet werden muss trotzdem jedes Plugin einzeln — erst
            dann lässt es sich einem Agent zuweisen.
          </DialogDescription>
        </DialogHeader>

        {katalog.isLoading ? (
          <p className="text-[13px] text-text3">Katalog wird gelesen …</p>
        ) : lesefehler ? (
          // Ein Lesefehler ist KEIN „nichts zu tun" — sonst hielte der Admin den Katalog für
          // vollständig freigeschaltet (derselbe Fehler wie ein leerer Zustand bei #279).
          <div data-testid="freischalten-error">
            <CatalogSourceAlert sources={katalog.data?.sources} />
            <p className="text-[13px] text-danger">
              Der Katalog konnte nicht gelesen werden
              {katalog.isError ? `: ${(katalog.error as Error | undefined)?.message}` : ""}. Es lässt sich
              gerade nicht sagen, welche Einträge noch freizuschalten sind — bitte später im Plugin-Katalog
              nachsehen.
            </p>
          </div>
        ) : (
          <ul className="max-h-64 space-y-1 overflow-auto">
            {offen.map((p) => (
              <li key={p.name} className="flex items-center gap-2.5 rounded-md border border-border px-2.5 py-1.5">
                <input
                  type="checkbox"
                  checked={!abgewaehlt.has(p.name)}
                  onChange={(e) =>
                    setAbgewaehlt((s) => {
                      const next = new Set(s);
                      if (e.target.checked) next.delete(p.name);
                      else next.add(p.name);
                      return next;
                    })
                  }
                  data-testid={`freischalten-${p.name}`}
                />
                <span className="text-[13px] font-medium text-text">{p.name}</span>
                {p.description && (
                  <span className="truncate text-[11.5px] text-text3" title={p.description}>
                    {p.description}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="freischalten-spaeter">
            Später
          </Button>
          <Button
            disabled={bulk.isPending || gewaehlt.length === 0}
            onClick={() =>
              bulk.mutate(
                { marketplaceId, pluginNames: gewaehlt.map((p) => p.name), approved: true },
                {
                  onSuccess: (r) => {
                    if (r.failed.length === 0) toast.success(`${r.done.length} Plugins freigeschaltet`);
                    else
                      toast.warning(
                        `${r.done.length} freigeschaltet, ${r.failed.length} nicht: ${r.failed.map((f) => f.name).join(", ")}`,
                      );
                    onOpenChange(false);
                  },
                  onError: (err) => toast.error(`Fehlgeschlagen: ${(err as Error).message}`),
                },
              )
            }
            data-testid="freischalten-confirm"
          >
            {gewaehlt.length === 1 ? "1 Plugin freischalten" : `${gewaehlt.length} Plugins freischalten`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
